// Per-client field overrides — sdílená cache mezi ClientDetail (kde se edit zapisuje)
// a Clients list (kde se musí okamžitě projevit, jinak po vyjetí z detailu user vidí
// starou hodnotu dokud sync klienti cron neproběhne — až 1 hodina).
//
// localStorage key: "fw40_contact_overrides_v1"  (legacy jméno, ale obsahuje VŠECHNA pole klienta)
// Format: { klientName: { fieldKey: value, ..., savedAt: timestamp } }
//
// Workflow:
//   1) ClientDetail edit Save → setClientOverride(klient, { balicek: "..." })
//   2) Clients list render → applyOverridesToList(clients) překryje pole klienta z localStorage
//   3) Sync cron Notion → Sheet aktualizuje hodnotu → na dalším GET /api/clients má parent prop
//      novou hodnotu → getClientOverride si všimne shody a override smaže (auto-cleanup)
//
// Max TTL: 24h (failsafe pro případ že sync nikdy neproběhne).

const OVERRIDES_STORAGE_KEY = "fw40_contact_overrides_v1";
const OVERRIDES_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function loadAll() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return {};
    const raw = window.localStorage.getItem(OVERRIDES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveAll(obj) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(obj));
  } catch { /* quota exceeded / private mode */ }
}

// Vrátí override pro klienta (po auto-cleanup synced poli a TTL check).
// currentValues = aktuální hodnoty z parent propu (pro detekci synced).
// Vrátí null pokud žádné pole nezůstalo nebo TTL vypršel.
export function getClientOverride(klientName, currentValues = {}) {
  if (!klientName) return null;
  const all = loadAll();
  const ov = all[klientName];
  if (!ov) return null;
  if (ov.savedAt && Date.now() - ov.savedAt > OVERRIDES_MAX_AGE_MS) {
    delete all[klientName];
    saveAll(all);
    return null;
  }
  const cleaned = {};
  let dirty = false;
  for (const [k, v] of Object.entries(ov)) {
    if (k === "savedAt") continue;
    const cur = currentValues?.[k];
    if ((v || "") === (cur || "")) { dirty = true; continue; }
    cleaned[k] = v;
  }
  if (Object.keys(cleaned).length === 0) {
    delete all[klientName];
    saveAll(all);
    return null;
  }
  if (dirty) {
    all[klientName] = { ...cleaned, savedAt: ov.savedAt };
    saveAll(all);
  }
  return cleaned;
}

export function setClientOverride(klientName, fields) {
  if (!klientName || !fields) return;
  const all = loadAll();
  const existing = all[klientName] || {};
  all[klientName] = { ...existing, ...fields, savedAt: Date.now() };
  saveAll(all);
}

// 20. 10. 2026: po neúspěšném uložení odebere optimisticky uložená pole, jinak by
// seznam i detail až 24 h ukazovaly hodnotu, která se do databáze nezapsala.
export function odeberClientOverride(klientName, keys) {
  if (!klientName || !Array.isArray(keys) || keys.length === 0) return;
  const all = loadAll();
  const ov = all[klientName];
  if (!ov) return;
  for (const k of keys) delete ov[k];
  if (Object.keys(ov).filter((k) => k !== "savedAt").length === 0) delete all[klientName];
  saveAll(all);
}

// Backwards-compat wrapper pro Modul 2A1 (kontakty)
export function getContactOverride(klientName, currentEmail, currentTelefon) {
  return getClientOverride(klientName, { email: currentEmail, telefon: currentTelefon });
}

export const setContactOverrideCache = setClientOverride;

// Hlavní helper pro Clients list: vezme array clients (z /api/clients) a překryje
// hodnoty z localStorage overrides. Použije se po každém načtení dat.
//
// Mapping fieldKey override → key v client objektu:
//   balicek → balicek, trener → trener, lokalita → lokalita,
//   typSpoluprace → typSpoluprace, zacatek → zacatek, konec → konec,
//   stage → stage, stavSpoluprace → stav (rozdíl jména!), blacklist → blacklist,
//   zpusobPlatby → zpusobPlatby, frekvence → frekvenceFakturace (rozdíl!),
//   kdoFakturuje → kdoFakturuje, castka → castkaMesic (rozdíl!),
//   email → email, telefon → telefon,
//   levelTreninku → levelTreninku, ... (1:1)
const FIELD_MAP = {
  // Override key → klient property key (kde se to v UI zobrazí)
  stavSpoluprace: "stav",
  frekvence: "frekvenceFakturace",
  castka: "castkaMesic",
};

export function applyOverridesToList(clients) {
  if (!Array.isArray(clients)) return clients;
  return clients.map((c) => {
    if (!c?.klient) return c;
    const ov = getClientOverride(c.klient, c);
    if (!ov) return c;
    const patched = { ...c };
    for (const [k, v] of Object.entries(ov)) {
      const targetKey = FIELD_MAP[k] || k;
      patched[targetKey] = v;
    }
    return patched;
  });
}
